// @givmo/app-charity — V2 statement headline facts (CPA-WEB13).
//
// This is deliberately separate from the legacy page's `DashboardTiles`. The one-cycle flag-OFF
// rollback keeps its five historic figures unchanged; the V2 statement renders only the four facts
// Jonathan selected after the DEV reveal. Every money value comes straight from the dashboard wire.
// There is no subtraction or fallback: an older response without `net_pending` fails visibly instead
// of inventing a zero or deriving one in the browser. Nothing renders beneath the four tiles: the
// "Fundraising platform: <name>." line was removed at Jonathan's 2026-09-04 walkthrough ("totally
// unnecessary") — `platform_legal_name` stays on the wire, the statement prints nothing from it.

import type { Section321DashboardOut } from '@givmo/api-client';
import { isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  EmptyState,
  Icon,
  Skeleton,
  StatCard,
  testIdProp,
} from '@givmo/design-system';
import { ShieldAlert } from 'lucide-react';
import { displayCount, displayDate, displayMoney } from './grants-decode';

const PREFIX = 'grants';

export function StatementHeadlineTiles({
  isPending,
  isError,
  error,
  dashboard,
  onRetry,
}: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  dashboard: Section321DashboardOut | null;
  onRetry: () => void;
}) {
  if (isPending) {
    return (
      <div
        className="grid grid-cols-2 gap-10 sm:gap-16 xl:grid-cols-4"
        {...testIdProp(`${PREFIX}-tiles-loading`)}
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index} className="p-12 sm:p-20">
            <Skeleton className="h-12 w-1/2" />
            <Skeleton className="mt-12 h-28 w-3/4" />
          </Card>
        ))}
      </div>
    );
  }

  const hasNetPending =
    typeof dashboard?.net_pending === 'string' && dashboard.net_pending.trim().length > 0;

  if (isError || !dashboard || !hasNetPending) {
    return (
      <EmptyState
        testId={`${PREFIX}-tiles-error`}
        icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
        title="Couldn’t load your totals"
        description={
          <span className="text-text-secondary">
            {!hasNetPending && dashboard
              ? 'We couldn’t load a complete set of donation totals. Please try again.'
              : isGivmoError(error)
                ? error.message
                : 'We couldn’t load your donation totals. Please try again.'}
          </span>
        }
        action={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onRetry}
            testId={`${PREFIX}-tiles-retry`}
          >
            Try again
          </Button>
        }
      />
    );
  }

  const periodLabel = formatPeriod(dashboard.period_start, dashboard.period_end);
  const cardClassName = 'gap-8 p-12 sm:gap-12 sm:p-20';

  return (
    <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-tiles`)}>
      <div className="grid grid-cols-2 gap-10 sm:gap-16 xl:grid-cols-4">
        <StatCard
          testId={`${PREFIX}-tile-count`}
          label="Number of donations"
          value={
            <span className="text-s20 sm:text-s30">{displayCount(dashboard.donation_count)}</span>
          }
          sublabel={periodLabel ? `Covered ${periodLabel}` : 'No donations yet'}
          className={cardClassName}
        />
        <StatCard
          testId={`${PREFIX}-tile-sent`}
          label="Net donations sent"
          value={<span className="text-s20 sm:text-s30">{displayMoney(dashboard.total_sent)}</span>}
          // GD-F-T20: `total_sent` counts a grant paid by check from its mailing, so a check still in
          // the mail is in this figure: "Transferred" alone would claim money not yet deposited.
          sublabel="Mailed or transferred to your organization"
          className={cardClassName}
        />
        <StatCard
          testId={`${PREFIX}-tile-pending`}
          label="Net donations pending"
          value={
            <span className="text-s20 sm:text-s30">{displayMoney(dashboard.net_pending)}</span>
          }
          // GD-F-T20: "sent", as the sent tile counts it. A check in the mail is sent but not yet
          // transferred, so "Not yet transferred" would also describe money this figure leaves out.
          sublabel="Not yet sent"
          className={cardClassName}
        />
        <StatCard
          testId={`${PREFIX}-tile-total`}
          label="Total donations"
          value={
            <span className="text-s20 sm:text-s30">{displayMoney(dashboard.net_after_fees)}</span>
          }
          // The two tiles beside it, in their own words: `net_after_fees` is `total_sent` plus
          // `net_pending` to the penny.
          sublabel="Net sent + pending"
          className={cardClassName}
        />
      </div>
    </div>
  );
}

function formatPeriod(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  const from = displayDate(start);
  const to = displayDate(end);
  if (from === '—' && to === '—') return null;
  if (from === to) return from;
  return `${from} – ${to}`;
}
